import React, { useEffect } from 'react';
import {
    Box, Grid, Typography, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, ListItemIcon
} from '@mui/material';
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import franchises from '../franchises';
import dayjs from 'dayjs';
import CustomSwitch from '../../../custom/customSwitch';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';

export default function BasicInfo({ formData, formErrors, zip, regions, selectedRegion, handleInputChange, handleZipInputChange,
                                      handleRegionChange, handleAccidentsCoverageChange, regionsLoading }) {
    useEffect(() => {
        const dob = formData.dateOfBirth;

        let defaultFranchise = 300;

        if (dob && dayjs(dob).isValid()) {
            const age = dayjs().diff(dayjs(dob), 'year');
            defaultFranchise = age < 18 ? 0 : 300;
        }

        if (formData.franchise !== defaultFranchise) {
            handleInputChange({
                target: {
                    name: 'franchise',
                    value: defaultFranchise
                }
            });
        }

    }, [formData.dateOfBirth]);
    return (
        <Grid container >
            <Grid item xs={12} md={6}>
                <Box>
                    <Typography variant="h5" sx={{ mt: 2, mb: 2 }}>
                        Grundlegende Benutzerinformationen
                    </Typography>

                    <Grid container direction="column" spacing={3}>

                        <Grid item>
                            <TextField
                                label="PLZ"
                                variant="standard"
                                name="zip"
                                value={zip || ''}
                                onChange={handleZipInputChange}
                                fullWidth
                                required
                                error={!!formErrors.zip}
                                helperText={formErrors.zip}
                                InputProps={{
                                    endAdornment: regionsLoading && (
                                        <CircularProgress size={20} />
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item>
                            <Box sx={{ width: 400 }}>
                                <FormControl
                                    variant="standard"
                                    fullWidth
                                >
                                    <InputLabel>Region</InputLabel>
                                    <Select
                                        value={selectedRegion}
                                        onChange={handleRegionChange}
                                        label="Region"
                                        disabled={regions.length === 0} // disables select if no regions
                                    >
                                        {regions.map((region) => (
                                            <MenuItem key={region.uid} value={region.uid}>
                                                {region.municipality}, {region.district}, {region.location}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {zip.length > 0 && regions.length === 0 && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ mt: 1, textAlign: 'left' }}
                                    >
                                        Failed to find region based on provided ZIP
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        <Grid item>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    value={formData.gender || 'male'}
                                    name="gender"
                                    label="Geschlecht"
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="male">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <MaleIcon fontSize="small" />
                                            <Box sx={{ ml: 1 }}>Männlich</Box>
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="female">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FemaleIcon fontSize="small" />
                                            <Box sx={{ ml: 1 }}>Weiblich</Box>
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="pregnant">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <PregnantWomanIcon fontSize="small" />
                                            <Box sx={{ ml: 1 }}>Schwanger</Box>
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Geburtsdatum"
                                    name="dateOfBirth"
                                    value={formData.dateOfBirth}
                                    onChange={(newValue) =>
                                        handleInputChange({ target: { name: 'dateOfBirth', value: newValue } })
                                    }
                                    format="DD-MM-YYYY"
                                    disableMaskedInput
                                    slotProps={{
                                        textField: {
                                            error: !!formErrors.dateOfBirth,
                                            helperText: formErrors.dateOfBirth,
                                            variant: "standard",
                                            fullWidth: true,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel>Franchise</InputLabel>
                                <Select
                                    value={formData.franchise}
                                    name="franchise"
                                    label="Franchise"
                                    onChange={handleInputChange}
                                >
                                    {franchises.map((franchise) => (
                                        <MenuItem key={franchise.uid} value={franchise.uid}>
                                            {franchise.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body1" sx={{ mr: 2 }}>
                                    Unfallversicherung:
                                </Typography>
                                <CustomSwitch
                                    checked={formData.accidentsCoverage || false}
                                    onChange={handleAccidentsCoverageChange}
                                    name="accidentsCoverage"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    );
}
